import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {BreadcrumbName} from 'common/src/modules/breadcrumbs/breadcrumbs.enums';
import {DPLayerMapping, DPMeta} from 'common/src/modules/dynamicParameters/dynamicParameters.types';
import {
  DPDynamicSourceType,
  SourceLayerType,
} from 'common/src/modules/dynamicParameters/dynamicParameters.enums';
import {TemplateMappingState} from './templateMapping.types';

export const templateMappingInitialState: TemplateMappingState = {
  templateMeta: {},
  templateMapping: {},
  originalMapping: {},
  completedFormats: {},
  isSupportMapping: false,
  isPlatformMapping: false,
  productCounts: {},
  isAllowedToUpdateProductCount: false,
  isSupportMappingTimeline: false,
  sourceTypeFilter: null,
  isMetaLoading: false,
  isV9: false,
  isOfflinePreview: false,
  formatsChanged: [],
  formatsProductCountChanged: [],
  supportedDynamicSourceTypes: [],
};

const templateMappingReducer = createSlice({
  name: BreadcrumbName.TemplateMapping,
  initialState: templateMappingInitialState,
  reducers: {
    resetState: () => templateMappingInitialState,

    setTemplateMeta: (state, action: PayloadAction<Record<string, DPMeta>>) => {
      state.templateMeta = action.payload;
    },

    updateTemplateMeta: (state, action: PayloadAction<{format: string; meta: DPMeta}>) => {
      state.templateMeta[action.payload.format] = action.payload.meta;
    },

    setSupportedDynamicSourceTypes: (state, action: PayloadAction<DPDynamicSourceType[]>) => {
      state.supportedDynamicSourceTypes = action.payload;
    },

    setTemplateMapping: (state, action: PayloadAction<Record<string, DPLayerMapping[]>>) => {
      state.templateMapping = action.payload;
    },

    updateTemplateMapping: (
      state,
      action: PayloadAction<{format: string; mapping: DPLayerMapping[]}>,
    ) => {
      state.templateMapping[action.payload.format] = action.payload.mapping;
    },

    setOriginalMapping: (state, action: PayloadAction<Record<string, DPLayerMapping[]>>) => {
      state.originalMapping = action.payload;
    },

    setCompletedFormats: (state, action: PayloadAction<Record<string, boolean>>) => {
      state.completedFormats = action.payload;
    },

    updateCompletedFormat: (
      state,
      action: PayloadAction<{format: string; isCompleted: boolean}>,
    ) => {
      state.completedFormats[action.payload.format] = action.payload.isCompleted;
    },

    setIsSupportMapping: (state, action: PayloadAction<boolean>) => {
      state.isSupportMapping = action.payload;
    },

    setIsPlatformMapping: (state, action: PayloadAction<boolean>) => {
      state.isPlatformMapping = action.payload;
    },

    setProductCounts: (state, action: PayloadAction<Record<string, number>>) => {
      state.productCounts = action.payload;
    },

    updateProductCount: (state, action: PayloadAction<{format: string; count: number}>) => {
      state.productCounts[action.payload.format] = action.payload.count;
    },

    setIsAllowedToUpdateProductCount: (state, action: PayloadAction<boolean>) => {
      state.isAllowedToUpdateProductCount = action.payload;
    },

    setIsSupportMappingTimeline: (state, action: PayloadAction<boolean>) => {
      state.isSupportMappingTimeline = action.payload;
    },

    setSourceTypeFilter: (state, action: PayloadAction<SourceLayerType | null>) => {
      state.sourceTypeFilter = action.payload;
    },

    setIsMetaLoading: (state, action: PayloadAction<boolean>) => {
      state.isMetaLoading = action.payload;
    },

    setIsV9: (state, action: PayloadAction<boolean>) => {
      state.isV9 = action.payload;
    },

    setIsOfflinePreview: (state, action: PayloadAction<boolean>) => {
      state.isOfflinePreview = action.payload;
    },

    setFormatsChanged: (state, action: PayloadAction<string[]>) => {
      state.formatsChanged = action.payload;
    },

    addFormatsChanged: (state, action: PayloadAction<string[]>) => {
      state.formatsChanged = [...new Set([...state.formatsChanged, ...action.payload])];
    },

    setFormatsProductCountChanged: (state, action: PayloadAction<string[]>) => {
      state.formatsProductCountChanged = action.payload;
    },

    addFormatsProductCountChanged: (state, action: PayloadAction<string[]>) => {
      state.formatsProductCountChanged = [
        ...new Set([...state.formatsProductCountChanged, ...action.payload]),
      ];
    },
  },
});

export const {
  resetState,
  setTemplateMeta,
  updateTemplateMeta,
  setTemplateMapping,
  updateTemplateMapping,
  setOriginalMapping,
  setCompletedFormats,
  updateCompletedFormat,
  setIsSupportMapping,
  setIsPlatformMapping,
  setProductCounts,
  updateProductCount,
  setIsAllowedToUpdateProductCount,
  setIsSupportMappingTimeline,
  setSourceTypeFilter,
  setIsMetaLoading,
  setIsV9,
  setIsOfflinePreview,
  setSupportedDynamicSourceTypes,
  setFormatsChanged,
  addFormatsChanged,
  setFormatsProductCountChanged,
  addFormatsProductCountChanged,
} = templateMappingReducer.actions;

export default templateMappingReducer.reducer;

export {loadTemplateMappingData} from './thunks/loadTemplateMappingData';
